import React, { useCallback } from "react";
import toaster, { ToastOptions } from "react-hot-toast";
import { MdClose } from "react-icons/md";

import { LayoutContext } from "@/contexts/LayoutContext";

import { Position, Toast } from "../types";

const closeButtonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: "4px",
  margin: "-4px -8px -4px 0",
  borderRadius: "50%",
};

const toastContentStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const CloseButton = ({ toastId, color }: { toastId: string; color: string }) => (
  <button
    style={closeButtonStyle}
    onClick={() => toaster.dismiss(toastId)}
    aria-label="Close toast"
  >
    <MdClose
      size={18}
      color={color}
    />
  </button>
);

const errorOptions = (
  position?: Position,
  marginBotton?: number,
  marginTop?: number,
  duration?: number,
  className?: string,
  id?: string
): ToastOptions => ({
  style: {
    background: "#ff7f7f",
    color: "white",
    marginBottom: marginBotton && `${marginBotton}px`,
    marginTop: marginTop && `${marginTop}px`,
  },
  iconTheme: {
    primary: "#fff",
    secondary: "#b30000",
  },
  position,
  duration,
  className,
  id: id || "error-toast",
});

const successOptions = (
  position?: Position,
  marginBotton?: number,
  marginTop?: number,
  duration?: number,
  className?: string,
  id?: string
): ToastOptions => ({
  style: {
    background: "#27bfb1",
    color: "white",
    fontWeight: "bold",
    marginBottom: marginBotton && `${marginBotton}px`,
    marginTop: marginTop && `${marginTop}px`,
  },
  iconTheme: {
    primary: "#fff",
    secondary: "#156a62",
  },
  position,
  duration,
  className,
  id: id || "success-toast",
});

const notificationOptions = (
  position?: Position,
  marginBotton?: number,
  marginTop?: number,
  duration?: number,
  className?: string,
  id?: string
): ToastOptions => ({
  style: {
    background: "#4799eb",
    color: "white",
    marginBottom: marginBotton && `${marginBotton}px`,
    marginTop: marginTop && `${marginTop}px`,
  },
  iconTheme: {
    primary: "#fff",
    secondary: "#0f4d8a",
  },
  position,
  duration,
  className,
  id: id || "notification-toast",
});

const customOptions = (
  position?: Position,
  marginBotton?: number,
  marginTop?: number,
  duration?: number,
  className?: string,
  id?: string
): ToastOptions => ({
  style: {
    marginBottom: marginBotton && `${marginBotton}px`,
    marginTop: marginTop && `${marginTop}px`,
  },
  position,
  duration,
  className,
  id: id || "custom-toast",
});

export const useToast = () => {
  const layoutContext = React.useContext(LayoutContext);
  const genericErrors = layoutContext?.genericErrors;

  const toast = useCallback(
    (props: Toast) => {
      const {
        type,
        marginBottom,
        marginTop,
        duration = 5000,
        position = Position.bottom,
        className,
        closable = false,
      } = props;

      if (type === "notification") {
        const message = genericErrors?.[props.message] || props.message;
        if (closable) {
          return toaster(
            (t) => (
              <div style={toastContentStyle}>
                <span>{message}</span>
                <CloseButton
                  toastId={t.id}
                  color="white"
                />
              </div>
            ),
            {
              ...notificationOptions(
                position,
                marginBottom,
                marginTop,
                duration,
                className,
                props.id
              ),
              id: props.id || `notification-toast-${Date.now()}`,
            }
          );
        } else {
          return toaster(
            message,
            notificationOptions(position, marginBottom, marginTop, duration, className, props.id)
          );
        }
      } else if (type === "success") {
        const message = genericErrors?.[props.message] || props.message;
        if (closable) {
          return toaster.success(
            (t) => (
              <div style={toastContentStyle}>
                <span>{message}</span>
                <CloseButton
                  toastId={t.id}
                  color="white"
                />
              </div>
            ),
            {
              ...successOptions(position, marginBottom, marginTop, duration, className, props.id),
              id: props.id || `success-toast-${Date.now()}`,
            }
          );
        } else {
          return toaster.success(
            message,
            successOptions(position, marginBottom, marginTop, duration, className, props.id)
          );
        }
      } else if (type === "error") {
        const message = genericErrors?.[props.message] || props.message;
        if (closable) {
          return toaster.error(
            (t) => (
              <div style={toastContentStyle}>
                <span>{message}</span>
                <CloseButton
                  toastId={t.id}
                  color="white"
                />
              </div>
            ),
            {
              ...errorOptions(position, marginBottom, marginTop, duration, className, props.id),
              id: props.id || `error-toast-${Date.now()}`,
            }
          );
        } else {
          return toaster.error(
            message,
            errorOptions(position, marginBottom, marginTop, duration, className, props.id)
          );
        }
      } else {
        return toaster.custom(
          props.content,
          customOptions(position, marginBottom, marginTop, duration, className, props.id)
        );
      }
    },
    [genericErrors]
  );

  return {
    toast,
  };
};
